import { API_BASE_URL } from "../config";
import { createAuthenticatedClient } from "../authenticatedClient";
import { safeValidate } from "../safeValidate";
import {
  configureSsoRequestSchema,
  configureSsoResponseSchema,
  messageResponseSchema,
  ssoConfigResponseSchema,
  ssoPreCheckResponseSchema,
  spMetadataResponseSchema,
  updateSsoRequestSchema,
  type ConfigureSsoRequest,
  type ConfigureSsoResponse,
  type SsoConfigResponse,
  type SsoMessageResponse,
  type SsoPreCheckResponse,
  type SpMetadataResponse,
  type UpdateSsoRequest,
} from "../schemas/sso.schema";

const ssoClient = createAuthenticatedClient(
  `${API_BASE_URL}/api/auth/admin/v1/sso`,
);

export const ssoApi = {
  /**
   * GET /api/auth/admin/v1/sso
   * Returns current SSO configuration or null if not configured (404).
   */
  getConfig: async (): Promise<SsoConfigResponse | null> => {
    try {
      const response = await ssoClient.get<SsoConfigResponse>("");
      return safeValidate(
        ssoConfigResponseSchema,
        response,
        "ssoApi.getConfig",
      );
    } catch (error: unknown) {
      if (
        error instanceof Error &&
        "status" in error &&
        (error as { status: number }).status === 404
      ) {
        return null;
      }
      throw error;
    }
  },

  /**
   * PUT /api/auth/admin/v1/sso
   * Configure and enable SSO for a tenant.
   */
  configure: async (
    data: ConfigureSsoRequest,
  ): Promise<ConfigureSsoResponse> => {
    const validated = configureSsoRequestSchema.parse(data);
    const response = await ssoClient.put<ConfigureSsoResponse>("", validated);
    return safeValidate(
      configureSsoResponseSchema,
      response,
      "ssoApi.configure",
    );
  },

  /**
   * PATCH /api/auth/admin/v1/sso
   * Update SSO settings (enable/disable, displayName, metadataUrl, emailAttribute).
   */
  update: async (data: UpdateSsoRequest): Promise<ConfigureSsoResponse> => {
    const validated = updateSsoRequestSchema.parse(data);
    const response = await ssoClient.patch<ConfigureSsoResponse>("", validated);
    return safeValidate(configureSsoResponseSchema, response, "ssoApi.update");
  },

  /**
   * GET /api/auth/admin/v1/sso/sp-metadata
   * Get SP metadata for IdP configuration.
   */
  getSpMetadata: async (): Promise<SpMetadataResponse> => {
    const response = await ssoClient.get<SpMetadataResponse>("/sp-metadata");
    return safeValidate(
      spMetadataResponseSchema,
      response,
      "ssoApi.getSpMetadata",
    );
  },

  /**
   * GET /api/auth/admin/v1/sso/precheck
   * Pre-check impact of enabling SSO for a tenant.
   */
  preCheck: async (): Promise<SsoPreCheckResponse> => {
    const response = await ssoClient.get<SsoPreCheckResponse>("/precheck");
    return safeValidate(ssoPreCheckResponseSchema, response, "ssoApi.preCheck");
  },

  /**
   * PUT /api/auth/admin/v1/sso/refresh
   * Refresh IdP metadata (re-fetches certs from metadata URL).
   */
  refreshMetadata: async (): Promise<SsoMessageResponse> => {
    const response = await ssoClient.put<SsoMessageResponse>("/refresh");
    return safeValidate(
      messageResponseSchema,
      response,
      "ssoApi.refreshMetadata",
    );
  },

  /**
   * GET /api/auth/admin/v1/sso/test
   * Initiates SSO test connection and returns the redirect URL.
   */
  testConnection: async (): Promise<{
    method: string;
    redirectUrl: string;
    displayName: string;
  }> => {
    const response = await ssoClient.get<{
      method: string;
      redirectUrl: string;
      displayName: string;
    }>("/test");
    return response;
  },
};
